import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Loader2,
  Mail,
} from "lucide-react";
import Seo from "../components/Seo";
import { BOOKING_URL } from "@/lib/constants";
import { EASE } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Professional Services",
  "Consulting",
  "Healthcare / Wellness",
  "Financial Services",
  "Real Estate",
  "Education",
  "Hospitality",
  "Home Services",
  "Technology",
  "Other",
];

const GOALS = [
  "Generate More Leads",
  "Build Credibility",
  "Explain My Services",
  "Book Appointments",
  "Sell Products / Services",
  "Automate My Business",
  "Replace My Current Website",
  "Tell My Story Better",
  "Not Sure Yet",
];

const STAGES = [
  "I don't have a website.",
  "I have a website, but it needs work.",
  "My website looks outdated.",
  "My website looks good but doesn't convert.",
  "My business has outgrown my website.",
  "I need more than a website.",
  "I'm not sure what I need.",
];

const STORY_FIELDS = [
  { key: "business_does", label: "What does your business do?", testid: "flow-business-does-input" },
  { key: "who_serve", label: "Who do you serve?", testid: "flow-who-serve-input" },
  { key: "why_choose", label: "Why do customers choose you today?", testid: "flow-why-choose-input" },
  { key: "wish_understood", label: "What do you wish more people understood about your business?", testid: "flow-wish-understood-input" },
];

const INVESTMENTS = [
  { id: "foundation", name: "FOUNDATION", range: "$3,500 – $6,500", description: null, highlight: false },
  { id: "growth", name: "GROWTH", range: "$6,500 – $10,000", description: null, highlight: false },
  {
    id: "signature",
    name: "SIGNATURE",
    range: "$10,000+",
    description:
      "For businesses that want AT Digital to understand the business, develop the story, design the customer journey, build the technology and connect the systems.",
    highlight: true,
  },
];

const HELP_LEVELS = [
  { id: "just-build", title: "JUST BUILD IT", desc: "I already know what I want.", badge: null },
  { id: "help-tell", title: "HELP ME TELL IT", desc: "I know my business, but I need help communicating it.", badge: null },
  {
    id: "signature",
    title: "UNDERSTAND IT AND TELL IT FOR ME",
    desc: "I want AT Digital to deeply understand the business and develop the complete experience.",
    badge: "AT DIGITAL SIGNATURE",
  },
];

const ENGAGEMENT_LABELS = {
  "just-build": "LAUNCH — We build it",
  "help-tell": "GROWTH — We help tell it",
  signature: "AT DIGITAL SIGNATURE",
};

const PACKAGE_MAP = {
  launch: { investment: "foundation", help: "just-build" },
  growth: { investment: "growth", help: "help-tell" },
  signature: { investment: "signature", help: "signature" },
};

const SIGNATURE_STEPS = ["UNDERSTAND", "DEFINE", "DESIGN", "BUILD", "AUTOMATE", "GROW"];

const PREP_QUESTIONS = [
  "Where do you want your business to be 12 months from now?",
  "What should a customer immediately understand about you?",
  "What is your current website or digital process failing to communicate?",
];

const TOTAL_STEPS = 8;

const inputCls =
  "w-full rounded-xl border border-navyline bg-ink/70 px-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-600 transition-colors duration-200 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

const OptionButton = ({ label, selected, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`group flex w-full items-center justify-between gap-4 rounded-xl border px-5 py-4 text-left transition-colors duration-200 ${
      selected
        ? "border-gold bg-gold/10 text-white"
        : "border-navyline bg-ink/50 text-slate-300 hover:border-gold/50 hover:bg-ink"
    }`}
  >
    <span className="text-sm font-medium sm:text-base">{label}</span>
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
        selected ? "border-gold bg-gold" : "border-navyline group-hover:border-gold/50"
      }`}
    >
      {selected && <Check className="h-3.5 w-3.5 text-ink" strokeWidth={3} />}
    </span>
  </button>
);

const stepTransition = {
  initial: { opacity: 0, x: 28 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -28 },
  transition: { duration: 0.28, ease: EASE },
};

export default function Start() {
  const [searchParams] = useSearchParams();
  const preset = PACKAGE_MAP[searchParams.get("package")] || {};

  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState("flow");
  const [businessType, setBusinessType] = useState("");
  const [customType, setCustomType] = useState("");
  const [goals, setGoals] = useState([]);
  const [stage, setStage] = useState("");
  const [website, setWebsite] = useState("");
  const [storyFields, setStoryFields] = useState({ business_does: "", who_serve: "", why_choose: "", wish_understood: "" });
  const [investment, setInvestment] = useState(preset.investment || null);
  const [helpLevel, setHelpLevel] = useState(preset.help || null);
  const [details, setDetails] = useState({ name: "", company: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [emailRequested, setEmailRequested] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const effectiveType = businessType === "Other" ? customType.trim() || "Other" : businessType;
  const investmentObj = INVESTMENTS.find((i) => i.id === investment);
  const isSignature = helpLevel === "signature" || investment === "signature";
  const storyComplete = storyFields.business_does.trim() && storyFields.who_serve.trim();

  const pickSingle = (setter) => (value) => {
    setter(value);
    setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL_STEPS)), 320);
  };

  const toggleGoal = (g) =>
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: details.name,
        company: details.company,
        email: details.email,
        phone: details.phone || null,
        current_website: website || null,
        business_type: effectiveType,
        goals,
        current_stage: stage,
        investment_range: investmentObj ? investmentObj.range : "Not Sure Yet",
        help_level: helpLevel,
        business_does: storyFields.business_does || null,
        who_serve: storyFields.who_serve || null,
        why_choose: storyFields.why_choose || null,
        wish_understood: storyFields.wish_understood || null,
        story: null,
        source: "project-flow",
      });
      setLeadId(res.data.id);
      setPhase("profile");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error("Something went wrong saving your project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const emailSummary = async () => {
    if (!leadId || emailSending || emailRequested) return;
    setEmailSending(true);
    try {
      await axios.post(`${API}/leads/${leadId}/email-summary`);
      setEmailRequested(true);
    } catch (err) {
      toast.error("We couldn't send the email just now. Your project profile is safe with us.");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <main data-testid="start-page" className="relative min-h-screen overflow-hidden">
      <Seo
        title="Tell Us Your Story | AT Digital"
        description="Start your AT Digital project in about two minutes — tell us about your business, choose your investment level, and get your project profile."
      />
      <div className="bg-blueprint absolute inset-0 opacity-40" aria-hidden="true" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(50% 40% at 50% 0%, rgba(212,175,55,0.08), transparent)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-2xl px-5 pb-24 pt-32 sm:px-8 sm:pt-36">
        {phase === "flow" && (
          <>
            <div className="mb-8" data-testid="flow-progress">
              <div className="flex items-center justify-between font-mono text-[10px] font-semibold tracking-[0.22em] text-slate-500">
                <span>TELL US YOUR STORY</span>
                <span>
                  STEP {step} OF {TOTAL_STEPS}
                </span>
              </div>
              <div className="mt-3 h-1 overflow-hidden rounded-full bg-navyline/60">
                <motion.div
                  className="h-full rounded-full bg-gold"
                  initial={false}
                  animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                  transition={{ duration: 0.3, ease: EASE }}
                />
              </div>
            </div>
            <div className="rounded-3xl border border-navyline bg-navycard p-7 sm:p-10">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div key="s1" {...stepTransition}>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                      Let's start with your business.
                    </h1>
                    <p className="mt-2 text-sm text-slate-400">What kind of business are you building?</p>
                    <div className="mt-7 space-y-3">
                      {BUSINESS_TYPES.map((t) => (
                        <OptionButton
                          key={t}
                          label={t}
                          testid={`flow-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          selected={businessType === t}
                          onClick={() => {
                            setBusinessType(t);
                            if (t !== "Other") setTimeout(() => setStep(2), 320);
                          }}
                        />
                      ))}
                    </div>
                    {businessType === "Other" && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                        <input
                          data-testid="flow-type-custom-input"
                          value={customType}
                          onChange={(e) => setCustomType(e.target.value)}
                          placeholder="Tell us what you do..."
                          className={`${inputCls} mt-4`}
                          autoFocus
                        />
                        <button
                          data-testid="flow-step1-continue"
                          onClick={() => setStep(2)}
                          disabled={!customType.trim()}
                          className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:bg-goldlight disabled:opacity-50"
                        >
                          Continue <ArrowRight className="h-4 w-4" />
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="s2" {...stepTransition}>
                    <StepHeader
                      title="What do you want your website to do?"
                      sub="Choose everything that applies."
                      onBack={() => setStep(1)}
                    />
                    <div className="mt-7 space-y-3">
                      {GOALS.map((g) => (
                        <OptionButton
                          key={g}
                          label={g}
                          testid={`flow-goal-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          selected={goals.includes(g)}
                          onClick={() => toggleGoal(g)}
                        />
                      ))}
                    </div>
                    <ContinueButton testid="flow-step2-continue" disabled={goals.length === 0} onClick={() => setStep(3)} />
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="s3" {...stepTransition}>
                    <StepHeader title="Where are you today?" onBack={() => setStep(2)} />
                    <div className="mt-7 space-y-3">
                      {STAGES.map((s, i) => (
                        <OptionButton
                          key={s}
                          label={s}
                          testid={`flow-stage-${i + 1}`}
                          selected={stage === s}
                          onClick={pickSingle(setStage).bind(null, s)}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div key="s4" {...stepTransition}>
                    <StepHeader
                      title="Show us what you're working with."
                      sub="Share your current website — optional."
                      onBack={() => setStep(3)}
                    />
                    <input
                      data-testid="flow-website-input"
                      type="url"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://yourcurrentsite.com"
                      className={`${inputCls} mt-7`}
                    />
                    {website.trim() && (
                      <motion.p
                        data-testid="flow-website-acknowledgement"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className="mt-4 flex items-center gap-2 text-sm text-gold"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Got it. We'll review this before your consultation.
                      </motion.p>
                    )}
                    <div className="mt-7 flex items-center gap-5">
                      <ContinueButton testid="flow-step4-continue" onClick={() => setStep(5)} />
                      <button
                        data-testid="flow-step4-skip"
                        onClick={() => setStep(5)}
                        className="text-sm font-medium text-slate-500 underline-offset-4 transition-colors duration-200 hover:text-gold hover:underline"
                      >
                        Skip for now
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 5 && (
                  <motion.div key="s5" {...stepTransition}>
                    <StepHeader
                      title="Tell us your story."
                      sub="Don't worry about explaining it perfectly. Understanding the business is our job."
                      onBack={() => setStep(4)}
                    />
                    <div className="mt-7 space-y-5">
                      {STORY_FIELDS.map((f) => (
                        <div key={f.key}>
                          <label htmlFor={f.key} className="mb-2 block text-sm font-semibold text-slate-200">
                            {f.label}
                          </label>
                          <textarea
                            id={f.key}
                            data-testid={f.testid}
                            rows={2}
                            value={storyFields[f.key]}
                            onChange={(e) => setStoryFields({ ...storyFields, [f.key]: e.target.value })}
                            className={`${inputCls} resize-y`}
                          />
                        </div>
                      ))}
                    </div>
                    <ContinueButton
                      testid="flow-step5-continue"
                      disabled={!storyComplete}
                      onClick={() => setStep(6)}
                    />
                  </motion.div>
                )}

                {step === 6 && (
                  <motion.div key="s6" {...stepTransition}>
                    <StepHeader
                      title="How much are you prepared to invest?"
                      onBack={() => setStep(5)}
                    />
                    <div className="mt-7 space-y-4">
                      {INVESTMENTS.map((inv) => (
                        <button
                          key={inv.id}
                          type="button"
                          data-testid={`flow-investment-${inv.id}`}
                          onClick={pickSingle(setInvestment).bind(null, inv.id)}
                          aria-pressed={investment === inv.id}
                          className={`relative w-full rounded-2xl border p-6 text-left transition-colors duration-200 ${
                            inv.highlight
                              ? investment === inv.id
                                ? "border-gold bg-gold/10 shadow-lg shadow-gold/10"
                                : "border-gold/60 bg-navy hover:border-gold"
                              : investment === inv.id
                                ? "border-gold bg-gold/10"
                                : "border-navyline bg-ink/50 hover:border-gold/50"
                          }`}
                        >
                          {inv.highlight && (
                            <span className="absolute -top-3 left-6 rounded-full bg-gold px-3 py-1 font-mono text-[9px] font-bold tracking-[0.18em] text-ink">
                              THE COMPLETE AT DIGITAL EXPERIENCE
                            </span>
                          )}
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className={`font-mono text-xs font-bold tracking-[0.22em] ${inv.highlight ? "text-gold" : "text-slate-300"}`}>
                                {inv.name}
                              </p>
                              <p className="mt-1.5 font-display text-2xl font-extrabold text-white">{inv.range}</p>
                            </div>
                            <span
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                                investment === inv.id ? "border-gold bg-gold" : "border-navyline"
                              }`}
                            >
                              {investment === inv.id && <Check className="h-4 w-4 text-ink" strokeWidth={3} />}
                            </span>
                          </div>
                          {inv.description && (
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">{inv.description}</p>
                          )}
                        </button>
                      ))}
                      <button
                        type="button"
                        data-testid="flow-investment-unsure"
                        onClick={pickSingle(setInvestment).bind(null, "unsure")}
                        className={`w-full rounded-xl border border-dashed px-5 py-4 font-mono text-[11px] font-semibold tracking-[0.18em] transition-colors duration-200 ${
                          investment === "unsure"
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-navyline text-slate-500 hover:border-gold/50 hover:text-slate-300"
                        }`}
                      >
                        I'M NOT SURE YET
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 7 && (
                  <motion.div key="s7" {...stepTransition}>
                    <StepHeader
                      title="How much help do you want from us?"
                      sub="This shapes how we approach your project."
                      onBack={() => setStep(6)}
                    />
                    <div className="mt-7 space-y-4">
                      {HELP_LEVELS.map((h) => (
                        <button
                          key={h.id}
                          type="button"
                          data-testid={`flow-help-${h.id}`}
                          onClick={pickSingle(setHelpLevel).bind(null, h.id)}
                          aria-pressed={helpLevel === h.id}
                          className={`relative w-full rounded-2xl border p-6 text-left transition-colors duration-200 ${
                            h.id === "signature"
                              ? helpLevel === h.id
                                ? "border-gold bg-gold/10 shadow-lg shadow-gold/10"
                                : "border-gold/60 bg-navy hover:border-gold"
                              : helpLevel === h.id
                                ? "border-gold bg-gold/10"
                                : "border-navyline bg-ink/50 hover:border-gold/50"
                          }`}
                        >
                          {h.badge && (
                            <span className="absolute -top-3 left-6 rounded-full bg-gold px-3 py-1 font-mono text-[9px] font-bold tracking-[0.18em] text-ink">
                              {h.badge}
                            </span>
                          )}
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className={`font-display text-base font-extrabold tracking-wide ${h.id === "signature" ? "text-gold" : "text-white"}`}>
                                {h.title}
                              </p>
                              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{h.desc}</p>
                            </div>
                            <span
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                                helpLevel === h.id ? "border-gold bg-gold" : "border-navyline"
                              }`}
                            >
                              {helpLevel === h.id && <Check className="h-4 w-4 text-ink" strokeWidth={3} />}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 8 && (
                  <motion.div key="s8" {...stepTransition}>
                    <StepHeader
                      title="Almost there."
                      sub="Where should we send your project profile?"
                      onBack={() => setStep(7)}
                    />
                    <form onSubmit={submit} className="mt-7 space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <input
                          data-testid="flow-name-input"
                          required
                          value={details.name}
                          onChange={(e) => setDetails({ ...details, name: e.target.value })}
                          placeholder="Name"
                          className={inputCls}
                        />
                        <input
                          data-testid="flow-company-input"
                          required
                          value={details.company}
                          onChange={(e) => setDetails({ ...details, company: e.target.value })}
                          placeholder="Company"
                          className={inputCls}
                        />
                        <input
                          data-testid="flow-email-input"
                          required
                          type="email"
                          value={details.email}
                          onChange={(e) => setDetails({ ...details, email: e.target.value })}
                          placeholder="Email"
                          className={inputCls}
                        />
                        <input
                          data-testid="flow-phone-input"
                          type="tel"
                          value={details.phone}
                          onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                          placeholder="Phone (optional)"
                          className={inputCls}
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="flow-submit-btn"
                        disabled={submitting}
                        className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink shadow-lg shadow-gold/20 transition-colors duration-200 hover:bg-goldlight disabled:opacity-60"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Building your profile...
                          </>
                        ) : (
                          <>
                            Build My Project Profile
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}

        {phase === "profile" && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
            <div
              data-testid="project-profile"
              className={`rounded-3xl border p-8 sm:p-10 ${
                isSignature ? "border-gold/60 bg-navy shadow-2xl shadow-gold/10" : "border-navyline bg-navycard"
              }`}
            >
              <p className="font-mono text-[11px] font-semibold tracking-[0.26em] text-gold">
                {isSignature ? "SIGNATURE PROJECT" : "YOUR PROJECT PROFILE"}
              </p>
              <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                {isSignature ? "You're Building More Than a Website." : "We have the beginning of your story."}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">
                {isSignature
                  ? "You've selected AT Digital's complete engagement. We'll use your consultation to understand your business, positioning, customers, story, systems and growth goals before determining what should be built."
                  : "Next, we'll talk about the business behind it."}
              </p>
              <dl className="mt-8 space-y-6 border-t border-navyline/70 pt-8">
                <ProfileRow label="BUSINESS" value={`${details.company} — ${effectiveType}`} />
                <ProfileRow label="PRIMARY GOAL" value={goals[0] || "To be defined"} />
                {goals.length > 1 && (
                  <div>
                    <dt className="font-mono text-[10px] font-semibold tracking-[0.22em] text-slate-500">ALSO IMPORTANT</dt>
                    <dd className="mt-3 space-y-2">
                      {goals.slice(1).map((g) => (
                        <p key={g} className="flex items-center gap-2.5 text-sm text-slate-200">
                          <Check className="h-4 w-4 text-gold" strokeWidth={2.5} />
                          {g}
                        </p>
                      ))}
                    </dd>
                  </div>
                )}
                <ProfileRow label="CURRENT STAGE" value={stage} />
                <ProfileRow
                  label="STORY ENGAGEMENT"
                  value={ENGAGEMENT_LABELS[helpLevel] || "To be determined together"}
                />
                <ProfileRow
                  label="INVESTMENT"
                  value={investmentObj ? investmentObj.range : "Not sure yet"}
                />
              </dl>
              {isSignature && (
                <div data-testid="signature-journey" className="mt-9 rounded-2xl border border-navyline bg-ink/50 p-6">
                  <div className="flex flex-col items-center gap-1.5">
                    {SIGNATURE_STEPS.map((s, i) => (
                      <motion.div
                        key={s}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + i * 0.1, duration: 0.3 }}
                        className="flex flex-col items-center"
                      >
                        <span className="font-mono text-xs font-bold tracking-[0.3em] text-gold">{s}</span>
                        {i < SIGNATURE_STEPS.length - 1 && <span className="my-1 h-4 w-px bg-gold/40" />}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 rounded-3xl border border-navyline bg-navycard p-8 text-center sm:p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">You've started.</h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                Your project profile is ready. The next step is a conversation about your business, your story and
                what we should build.
              </p>
              <div className="mt-8 flex flex-col items-center gap-4">
                <button
                  data-testid="profile-book-call-btn"
                  onClick={() => setPhase("booking")}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink shadow-lg shadow-gold/20 transition-colors duration-200 hover:bg-goldlight"
                >
                  {isSignature ? "Book My Signature Strategy Call" : "Book My Strategy Session"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
                <button
                  data-testid="profile-email-summary-btn"
                  onClick={emailSummary}
                  disabled={emailSending || emailRequested}
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 underline-offset-4 transition-colors duration-200 hover:text-gold hover:underline disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />
                  {emailRequested
                    ? `Sent — check ${details.email}`
                    : emailSending
                      ? "Sending..."
                      : "Email me my project summary"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {phase === "booking" && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
            <h1 className="text-center font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Pick your time.
            </h1>
            <p className="mx-auto mt-3 max-w-md text-center text-sm text-slate-400">
              Your project profile is saved. Choose a time and we'll come prepared.
            </p>
            <div
              data-testid="booking-embed"
              className="mt-8 overflow-hidden rounded-3xl border border-navyline bg-white shadow-2xl shadow-black/40"
            >
              <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
                <p className="font-display text-sm font-bold text-ink">Book your strategy call</p>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-open-external-link"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal transition-colors duration-200 hover:text-golddeep"
                >
                  Open in new tab
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
              <iframe
                src={BOOKING_URL}
                title="Book a strategy call with AT Digital"
                data-testid="booking-iframe"
                loading="lazy"
                className="h-[680px] w-full border-0"
              />
            </div>
            <div className="mt-6 text-center">
              <button
                data-testid="booking-done-btn"
                onClick={() => {
                  setPhase("booked");
                  window.scrollTo({ top: 0 });
                }}
                className="text-sm font-medium text-slate-400 underline-offset-4 transition-colors duration-200 hover:text-gold hover:underline"
              >
                I've booked my session — see what's next
              </button>
            </div>
          </motion.div>
        )}

        {phase === "booked" && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
            <div data-testid="booked-confirmation" className="rounded-3xl border border-gold/50 bg-navy p-8 text-center sm:p-12">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.4} />
              <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                Your strategy session is booked.
              </h1>
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">
                We already know a little about your business. Now we'll dig into the story.
              </p>
            </div>
            <div className="mt-8 rounded-3xl border border-navyline bg-navycard p-8 sm:p-10">
              <p className="font-mono text-[11px] font-semibold tracking-[0.26em] text-gold">YOUR PROJECT PROFILE</p>
              <div className="mt-6 space-y-5">
                <ProfileRow label="BUSINESS" value={`${details.company} — ${effectiveType}`} />
                <ProfileRow
                  label="STORY ENGAGEMENT"
                  value={ENGAGEMENT_LABELS[helpLevel] || "To be determined together"}
                />
                <ProfileRow
                  label="INVESTMENT"
                  value={investmentObj ? investmentObj.range : "Not sure yet"}
                />
              </div>
              <div className="mt-9 border-t border-navyline/70 pt-8">
                <p className="font-mono text-[11px] font-semibold tracking-[0.26em] text-slate-500">BEFORE WE MEET</p>
                <p className="mt-3 text-sm text-slate-400">Think about these three questions:</p>
                <ol className="mt-5 space-y-4">
                  {PREP_QUESTIONS.map((q, i) => (
                    <li key={q} className="flex items-start gap-4">
                      <span className="font-mono text-sm font-bold text-gold">{i + 1}.</span>
                      <span className="font-display text-base font-medium leading-snug text-slate-200">{q}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}

const StepHeader = ({ title, sub, onBack }) => (
  <div>
    {onBack && (
      <button
        data-testid="flow-back-btn"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-200 hover:text-gold"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>
    )}
    <h1 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h1>
    {sub && <p className="mt-2 text-sm text-slate-400">{sub}</p>}
  </div>
);

const ContinueButton = ({ onClick, disabled, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:bg-goldlight disabled:opacity-50"
  >
    Continue <ArrowRight className="h-4 w-4" />
  </button>
);

const ProfileRow = ({ label, value }) => (
  <div>
    <dt className="font-mono text-[10px] font-semibold tracking-[0.22em] text-slate-500">{label}</dt>
    <dd className="mt-1.5 font-display text-lg font-semibold text-white">{value}</dd>
  </div>
);
